import {
  Box,
  Chip,
  Divider,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from "@mui/material";
import { memo, useCallback, useMemo, useState } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import axios from "axios";
import { nuclearn_api_url } from "src/App";
import {
  FfnRecordDetail,
  useFfnRecordStore,
} from "../../stores/FfnRecordStore";
import { downloadReportArtifact } from "../../records/downloadReportArtifact";
import { normalizeMultiline } from "../../records/normalizeMultiline";
import { AgentMarkdown } from "../../records/AgentMarkdown";

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  applicability: "Applicability",
  screening: "Screening",
  evaluation: "Evaluation",
  complete: "Complete",
  not_applicable: "Not Applicable",
  screened_out: "Screened Out",
  cancelled: "Cancelled",
};

const STEPS = [
  "Applicability",
  "Screening",
  "Evaluation",
  "Challenge",
  "Report",
] as const;

const SCREENING_QUESTIONS: Array<{ key: string; label: string }> = [
  { key: "is_change_to_facility_as_described", label: "Q1 Facility change" },
  {
    key: "is_change_to_procedures_as_described",
    label: "Q2 Procedure change",
  },
  {
    key: "is_adverse_change_to_methods_of_evaluation",
    label: "Q3 Methods of evaluation",
  },
  {
    key: "is_test_or_experiment_not_described",
    label: "Q4 Test or experiment",
  },
];

/** Derive the active stepper index from record state. */
const activeStep = (record: FfnRecordDetail): number => {
  if (record.report_artifact_id) return 5; // all done
  if (record.challenge_recorded && record.challenge_passed) return 4;
  switch (record.status) {
    case "draft":
      return 0;
    case "screening":
      return 1;
    case "evaluation":
      return record.evaluation_criteria_missing.length === 0 ? 3 : 2;
    case "screened_out":
    case "not_applicable":
    case "complete":
      return 3;
    default:
      return 0;
  }
};

const VerdictChip = ({
  verdict,
}: {
  verdict: "pass" | "weak" | "fail" | "not_applicable";
}) => {
  const map = {
    pass: { label: "PASS", color: "success" as const },
    weak: { label: "WEAK", color: "warning" as const },
    fail: { label: "FAIL", color: "error" as const },
    not_applicable: { label: "N/A", color: "default" as const },
  };
  const cfg = map[verdict] ?? map.not_applicable;
  return (
    <Chip
      label={cfg.label}
      color={cfg.color}
      size="small"
      variant={verdict === "not_applicable" ? "outlined" : "filled"}
      sx={{ fontWeight: 600, minWidth: 52, flexShrink: 0 }}
    />
  );
};

const YesNoChip = ({ value }: { value: boolean | undefined }) => {
  if (value === undefined) {
    return (
      <RemoveCircleOutlineIcon
        fontSize="small"
        sx={{ color: "text.disabled" }}
      />
    );
  }
  return value ? (
    <Chip label="YES" color="warning" size="small" sx={{ fontWeight: 600 }} />
  ) : (
    <Chip label="NO" size="small" sx={{ fontWeight: 600 }} />
  );
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="overline"
    sx={{ display: "block", color: "text.secondary", lineHeight: 2, mt: 1.5 }}
  >
    {children}
  </Typography>
);

/**
 * FfnRecordPanel — sidebar surface for the 50.59 record the agent is
 * working. Mirrors record tool responses live; refresh re-reads the API.
 */
const FfnRecordPanel = memo(() => {
  const { record, dialogueId, setRecord } = useFfnRecordStore();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const handleRefresh = useCallback(async () => {
    if (!record || dialogueId === null) return;
    setIsRefreshing(true);
    try {
      const response = await axios.get(
        `${nuclearn_api_url}/v1/fifty-fifty-nine/records/${record.record_id}`,
      );
      const fresh = response.data;
      setRecord(dialogueId, {
        ...record,
        ...fresh,
        record_id: fresh.id ?? record.record_id,
        panel_type: "ffn_record",
        report_artifact_id: fresh.report_artifact_id,
        evaluation_criteria_missing:
          fresh.evaluation_criteria_missing ??
          record.evaluation_criteria_missing,
      });
    } catch {
      // Leave the streamed state in place.
    } finally {
      setIsRefreshing(false);
    }
  }, [record, dialogueId, setRecord]);

  const handleRunVerification = useCallback(async () => {
    if (!record || dialogueId === null) return;
    setIsVerifying(true);
    try {
      const response = await axios.post(
        `${nuclearn_api_url}/v1/fifty-fifty-nine/records/${record.record_id}/verify`,
      );
      const fresh = response.data;
      setRecord(dialogueId, {
        ...record,
        report_verification: fresh.report_verification,
      });
    } catch {
      // Leave the streamed state in place; refresh re-reads the API.
    } finally {
      setIsVerifying(false);
    }
  }, [record, dialogueId, setRecord]);

  const failedGrades = useMemo(() => {
    if (!record?.challenge?.grades) return [];
    return record.challenge.grades.filter(
      (g) => g.verdict === "fail" || g.verdict === "weak",
    );
  }, [record?.challenge?.grades]);

  if (!record) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No 50.59 record is active in this conversation.
        </Typography>
      </Box>
    );
  }

  const verification = record.report_verification;
  const criteria = record.evaluation ?? {};
  const screening = record.screening ?? {};
  const evaluationApplicable =
    record.status === "evaluation" ||
    record.evaluation_criteria_recorded.length > 0;

  return (
    <Box sx={{ p: 2, overflowY: "auto" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {record.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Record #{record.record_id}
            {record.plant_name ? ` — ${record.plant_name}` : ""}
          </Typography>
        </Box>
        <Chip
          label={STATUS_LABELS[record.status] ?? record.status}
          size="small"
          color={
            record.status === "complete"
              ? "success"
              : record.status === "cancelled"
                ? "default"
                : "primary"
          }
          variant="outlined"
        />
        <Tooltip title="Refresh from record">
          <IconButton
            size="small"
            onClick={handleRefresh}
            disabled={isRefreshing}
            data-attr="ffn-panel:btn:refresh"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stepper */}
      <Stepper
        activeStep={activeStep(record)}
        orientation="horizontal"
        alternativeLabel
        sx={{ mt: 2, "& .MuiStepLabel-label": { fontSize: 11 } }}
      >
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Divider sx={{ my: 1.5 }} />

      {/* Applicability */}
      <SectionTitle>Applicability (NEI 96-07 §4.1)</SectionTitle>
      {record.applicability ? (
        <Typography variant="body2">
          10 CFR 50.59{" "}
          {record.applicability.fifty_fifty_nine_applies
            ? "applies"
            : "does not apply"}
          {record.applicability.controlling_regulation
            ? ` — controlled by ${record.applicability.controlling_regulation}`
            : ""}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Not yet recorded
        </Typography>
      )}

      {/* Screening */}
      <SectionTitle>Screening (§4.2)</SectionTitle>
      {record.screening ? (
        <>
          {SCREENING_QUESTIONS.map((q) => (
            <Box
              key={q.key}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: 0.4,
              }}
            >
              <Typography variant="body2">{q.label}</Typography>
              <YesNoChip value={screening[q.key]} />
            </Box>
          ))}
          <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
            {screening.screens_in
              ? "Screens IN — evaluation required"
              : "Screens OUT"}
          </Typography>
        </>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Not yet recorded
        </Typography>
      )}

      {/* Evaluation */}
      {evaluationApplicable && (
        <>
          <SectionTitle>Evaluation — 8 criteria (§4.3)</SectionTitle>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => {
              const answer = criteria?.[String(n)];
              const recorded = record.evaluation_criteria_recorded.includes(n);
              return (
                <Tooltip
                  key={n}
                  title={
                    !recorded
                      ? `Criterion ${n}: not yet recorded`
                      : answer?.criterion_met
                        ? `Criterion ${n}: MET — prior NRC approval required`
                        : `Criterion ${n}: not met`
                  }
                >
                  <Chip
                    label={n}
                    size="small"
                    color={
                      !recorded
                        ? "default"
                        : answer?.criterion_met
                          ? "error"
                          : "success"
                    }
                    variant={recorded ? "filled" : "outlined"}
                    icon={
                      !recorded ? undefined : answer?.criterion_met ? (
                        <CancelIcon />
                      ) : (
                        <CheckCircleIcon />
                      )
                    }
                  />
                </Tooltip>
              );
            })}
          </Box>
        </>
      )}

      {/* Challenge */}
      <SectionTitle>Independent Challenge</SectionTitle>
      {record.challenge_recorded ? (
        <>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
            <Chip
              label={record.challenge_passed ? "PASSED" : "FAILED"}
              color={record.challenge_passed ? "success" : "error"}
              size="small"
              sx={{ fontWeight: 600 }}
            />
            {record.challenge_stale && (
              <Chip
                label="STALE — regrade needed"
                color="warning"
                size="small"
              />
            )}
          </Box>
          {failedGrades.map((grade) => (
            <Box
              key={grade.item_id}
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
                py: 0.5,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <VerdictChip verdict={grade.verdict} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {grade.item_id}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ whiteSpace: "pre-wrap" }}
                >
                  {normalizeMultiline(
                    grade.required_revision || grade.evidence,
                  )}
                </Typography>
              </Box>
            </Box>
          ))}
          {record.challenge_summary && (
            <AgentMarkdown
              text={record.challenge_summary}
              sx={{ fontSize: 12, mt: 1 }}
            />
          )}
          {record.challenge?.narrative && (
            <Box
              component="details"
              sx={{
                mt: 1,
                "& summary": {
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "text.secondary",
                },
              }}
            >
              <summary>Full independent review</summary>
              <AgentMarkdown
                text={record.challenge.narrative}
                sx={{
                  fontSize: 12,
                  mt: 0.5,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                }}
              />
            </Box>
          )}
        </>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Not yet performed — required before finalization
        </Typography>
      )}

      {/* Report */}
      <SectionTitle>Report</SectionTitle>
      {record.report_artifact_id ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Chip
            label="Download report"
            color="primary"
            size="small"
            clickable
            onClick={() =>
              downloadReportArtifact(
                record.report_artifact_id as number,
                `50_59_record_${record.record_id}_report.docx`,
              )
            }
            data-attr="ffn-panel:link:report-download"
          />
          {verification?.status === "pending" ? (
            <Chip label="Verifying…" size="small" variant="outlined" />
          ) : (
            <Chip
              label={
                isVerifying
                  ? "Starting…"
                  : verification
                    ? "Re-run verification"
                    : "Run verification"
              }
              size="small"
              variant="outlined"
              clickable={!isVerifying}
              disabled={isVerifying}
              onClick={handleRunVerification}
              data-attr="ffn-panel:btn:run-verification"
            />
          )}
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Not yet finalized
        </Typography>
      )}
      {verification && verification.status !== "pending" && (
        <Typography
          variant="caption"
          color={verification.status === "failed" ? "error" : "text.secondary"}
          sx={{ display: "block", mt: 0.5 }}
        >
          {verification.status === "failed"
            ? "Verification failed — re-run"
            : `Claims verification: ${verification.tier ?? "complete"}`}
        </Typography>
      )}

      {typeof record.research_log_entries === "number" && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1.5 }}
        >
          {record.research_log_entries} research log entr
          {record.research_log_entries === 1 ? "y" : "ies"}
        </Typography>
      )}
    </Box>
  );
});

FfnRecordPanel.displayName = "FfnRecordPanel";

export default FfnRecordPanel;
